import { Component, computed, ElementRef, inject, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToolJobService } from '../../services/api-services/tools/tool-job.service';
import { CapabilityStore } from '../../services/capability.store';
import { routeId } from '../../utils/route-param.util';
import {
  JobPageHeaderComponent,
  JobPageHeaderState,
} from './job-page-header/job-page-header.component';
import { JobTimelineComponent } from './job-timeline/job-timeline.component';
import { scrollTimeline } from './job-timeline/job-timeline-scroll.util';

@Component({
  selector: 'app-job-page',
  templateUrl: './job-page.component.html',
  styleUrls: ['./job-page.component.scss'],
  imports: [JobTimelineComponent, JobPageHeaderComponent],
})
export class JobPageComponent {
  /** Services. */
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toolJobService = inject(ToolJobService);
  private readonly capabilityStore = inject(CapabilityStore);

  /** State. */
  private readonly toolId = routeId(this.route.snapshot.paramMap, 'toolId') ?? 0;
  readonly jobId = this.route.snapshot.paramMap.get('jobId');
  readonly numericJobId = routeId(this.route.snapshot.paramMap, 'jobId');
  readonly toolJob = this.toolJobService.getJobById(this.toolId, this.numericJobId);
  readonly isJobLoading = this.toolJob.isLoading;
  readonly jobError = this.toolJob.error;
  readonly isStartingJob = signal(false);

  /** Returns the selected tool. */
  readonly tool = computed(() => this.capabilityStore.tool(this.toolId));

  /** Checks whether the job is loading for the first time. */
  readonly isInitialLoading = computed(() => this.isJobLoading() && !this.toolJob.value());

  /** Returns the data consumed by the job page header. */
  readonly headerState = computed<JobPageHeaderState>(() => ({
    isTool: true,
    jobId: this.jobId,
    resource: this.capabilityStore.tool(this.toolId),
    run: this.toolJob.value(),
    loading: this.isJobLoading(),
    failed: !!this.jobError(),
    waitingForInput: false,
    starting: this.isStartingJob(),
    reload: () => this.toolJob.reload(),
  }));

  /** Constructor. */
  constructor() {
    scrollTimeline(() => {
      const job = this.toolJob.value();
      const paramSelector = !this.jobId && this.tool() ? 'app-input-parameter-node' : '';
      const selector = job ? 'app-tool-node' : paramSelector;
      if (!selector) return undefined;

      const element = this.element.querySelector<HTMLElement>(selector);
      if (!element) return undefined;

      return {
        element,
        key: job ? `tool-${job.toolJobId}-${job.status}` : selector,
      };
    });
  }

  /** Starts a job for the selected tool. */
  readonly startToolJob = async (form: FormGroup): Promise<void> => {
    const toolId = this.toolId;
    const result = await this.toolJobService.createJob(toolId, form);
    await this.router.navigate(['/tools', toolId, 'results', result.jobId]);
  };
}
